'use client';

import { useCallback, useEffect, useState, type ReactNode } from 'react';

import {
  executeBusinessSourceRetention,
  getBusinessDataIntegrity,
  setBusinessSourceLegalHold,
  type BusinessDataIntegrityOverview,
} from '../lib/api/business-reports';
import { AdminAlertModal, Loader } from './common';

type IntegrityAlert = {
  type: 'success' | 'error' | 'info' | 'warning';
  title: string;
  message: string;
};

export function BusinessDataIntegrityTab({
  active,
  token,
  canExecute,
  onTopBarActionsChange,
}: {
  active: boolean;
  token?: string;
  canExecute: boolean;
  onTopBarActionsChange?: (actions: ReactNode | null) => void;
}) {
  const [report, setReport] = useState<BusinessDataIntegrityOverview | null>(null);
  const [busy, setBusy] = useState('');
  const [approvalReference, setApprovalReference] = useState('');
  const [reason, setReason] = useState('');
  const [limit, setLimit] = useState(500);
  const [confirmed, setConfirmed] = useState(false);
  const [rowId, setRowId] = useState('');
  const [holdEnabled, setHoldEnabled] = useState(true);
  const [holdReason, setHoldReason] = useState('');
  const [alert, setAlert] = useState<IntegrityAlert | null>(null);

  const load = useCallback(async () => {
    setBusy('Loading data-integrity controls...');
    try {
      setReport(await getBusinessDataIntegrity(token));
    } catch (error) {
      setAlert({
        type: 'error',
        title: 'Could not load data integrity',
        message: errorMessage(error),
      });
    } finally {
      setBusy('');
    }
  }, [token]);

  useEffect(() => {
    if (active) void load();
  }, [active, load]);

  useEffect(() => {
    if (!active || !onTopBarActionsChange) return undefined;
    onTopBarActionsChange(
      <button
        className="btn btn-primary compact-btn business-integrity-refresh-action"
        type="button"
        disabled={Boolean(busy)}
        onClick={() => void load()}
      >
        Refresh
      </button>,
    );
    return () => onTopBarActionsChange(null);
  }, [active, busy, load, onTopBarActionsChange]);

  if (!active) return null;

  const execute = async () => {
    setBusy('Applying approved retention policy...');
    try {
      const result = await executeBusinessSourceRetention({
        confirmed,
        approvalReference: approvalReference.trim(),
        reason: reason.trim(),
        limit,
      }, token);
      setApprovalReference('');
      setReason('');
      setConfirmed(false);
      await load();
      setAlert({
        type: 'success',
        title: 'Retention execution completed',
        message: `${result.previewRowsDeleted} expired staging row(s) were deleted across ${result.previewRecordsAffected} preview(s), and ${result.sourcePayloadsAnonymized} aged source payload(s) were anonymized. Confirmed import metadata, normalized facts and audit evidence were preserved.`,
      });
    } catch (error) {
      setAlert({ type: 'error', title: 'Retention was not executed', message: errorMessage(error) });
    } finally {
      setBusy('');
    }
  };

  const changeHold = async () => {
    setBusy(`${holdEnabled ? 'Applying' : 'Releasing'} legal hold...`);
    try {
      await setBusinessSourceLegalHold(
        rowId.trim(),
        { enabled: holdEnabled, reason: holdReason.trim() },
        token,
      );
      setRowId('');
      setHoldReason('');
      await load();
      setAlert({
        type: 'success',
        title: `Legal hold ${holdEnabled ? 'applied' : 'released'}`,
        message: 'The source row state was changed through the mandatory audited database function.',
      });
    } catch (error) {
      setAlert({ type: 'error', title: 'Legal hold was not changed', message: errorMessage(error) });
    } finally {
      setBusy('');
    }
  };

  const importRetentionPolicy = report?.retentionPolicies.find(
    (policy) => policy.recordKey === 'BR-8-3-IMPORT-RETENTION',
  );

  return (
    <section className="tab-content business-source-page business-data-integrity-page" id="tab-business-data-integrity">
      {busy && <Loader message={busy} />}

      <div className="business-finance-kpi-grid business-integrity-kpis">
        <Kpi label="Source observations retained" value={report?.sourceRows.total ?? 0} />
        <Kpi label="Protected payloads retained" value={report?.sourceRows.sensitive ?? 0} />
        <Kpi label="Rows under legal hold" value={report?.sourceRows.legalHold ?? 0} />
        <Kpi label="Payloads anonymized" value={report?.sourceRows.disposed ?? 0} />
        <Kpi label="Expired staging rows" value={report?.sourceRows.expiredPreviewRows ?? 0} />
      </div>

      <div className="enterprise-card business-integrity-storage-note">
        <strong>Retention inventory — not incident alerts</strong>
        <span>These figures describe protected evidence held by the system without exposing patient/source payloads. Repeated source observations may be retained for audit while exact duplicate facts are safely ignored.</span>
      </div>

      <div className="enterprise-card business-integrity-storage-note">
        <strong>Report/export storage</strong>
        <span>{report?.generatedReportStorage.behavior || 'Loading...'}</span>
      </div>

      <section className="enterprise-card business-integrity-card">
        <CardHeading
          title="Governed retention policies"
          description="Draft means the retention durations still require a Finance/Privacy maker-checker decision. It is a governance state, not a data-integrity alarm."
          action={<a className="btn btn-outline btn-sm" href="/dashboard/business-reports/governance-ownership">Open Governance &amp; Ownership</a>}
        />
        <div className="business-finance-table-wrap">
          <table className="business-finance-table">
            <thead><tr><th>Policy</th><th>Status</th><th>Rule</th><th>Days</th><th>Disposition</th></tr></thead>
            <tbody>
              {(report?.retentionPolicies || []).flatMap((policy) => policy.retentionConfiguration.map((rule, index) => (
                <tr key={`${policy.recordKey}-${rule.recordType}`}>
                  <td>{index === 0 ? <><strong>{policy.title}</strong><small className="business-finance-subline">{policy.recordKey} · v{policy.version}</small></> : ''}</td>
                  <td>{index === 0 ? <span className={`status-badge ${policy.isEffective ? 'active' : 'pending'}`}>{policy.isEffective ? 'EFFECTIVE' : policy.status === 'DRAFT' ? 'AWAITING APPROVAL' : policy.status}</span> : ''}</td>
                  <td>{rule.recordType}</td>
                  <td>{rule.retentionDays}</td>
                  <td>{rule.disposition}</td>
                </tr>
              )))}
              {!report?.retentionPolicies.length && <tr><td colSpan={5}>Retention governance records are missing.</td></tr>}
            </tbody>
          </table>
        </div>
      </section>

      <section className="enterprise-card business-integrity-card business-integrity-policy-card">
        <CardHeading
          title="Retention eligibility preview"
          description="Read-only candidate counts under the currently configured import-retention durations. Nothing is deleted or anonymized by this preview."
          action={<span className={`status-badge ${importRetentionPolicy?.isEffective ? 'active' : 'pending'}`}>{importRetentionPolicy?.isEffective ? 'EXECUTION ENABLED' : 'APPROVAL REQUIRED'}</span>}
        />
        <div className="business-finance-kpi-grid business-integrity-kpis">
          <Kpi label="Expired staging rows eligible" value={report?.retentionEligibility.previewRows ?? 0} />
          <Kpi label="Aged protected rows eligible" value={report?.retentionEligibility.agedProtectedRows ?? 0} />
        </div>
        <div className="business-integrity-policy-copy">
          Execution preserves confirmed import batches, normalized source rows, financial facts and mandatory audit evidence.
        </div>
      </section>

      <section className="enterprise-card business-integrity-card business-integrity-policy-card">
        <CardHeading
          title="Fact correction policy"
          description="Imported facts remain immutable. Corrections and reversals must be separately linked and maker-checker approved."
          action={<span className={`status-badge ${report?.correctionPolicy?.isActive ? 'active' : 'pending'}`}>{report?.correctionPolicy?.isActive ? 'ACTIVE' : report?.correctionPolicy?.approvalStatus || 'MISSING'}</span>}
        />
        <div className="business-integrity-policy-copy">
          {report?.correctionPolicy?.description || 'Correction governance is not configured.'}
        </div>
      </section>

      {canExecute && (
        <div className="business-finance-two-column business-integrity-action-grid">
          <section className="enterprise-card business-integrity-card business-integrity-action-card">
            <CardHeading
              title="Execute approved retention"
              description="Deletes eligible transient staging rows and anonymizes eligible aged sensitive payloads. The server rejects execution until BR-8-3-IMPORT-RETENTION is approved, active and effective, and its reference matches."
            />
            <div className="business-finance-form-grid business-integrity-form-grid">
              <label>Approval reference<input value={approvalReference} onChange={(event) => setApprovalReference(event.target.value)} /></label>
              <label>Maximum rows<input type="number" min={1} max={5000} value={limit} onChange={(event) => setLimit(Number(event.target.value))} /></label>
              <label className="full">Execution reason<textarea value={reason} onChange={(event) => setReason(event.target.value)} /></label>
              <label className="full business-integrity-confirmation">
                <input type="checkbox" checked={confirmed} onChange={(event) => setConfirmed(event.target.checked)} />
                <span>I understand eligible staging rows will be deleted and eligible aged source payloads will be irreversibly anonymized while confirmed imports, normalized facts and audit evidence remain.</span>
              </label>
            </div>
            <div className="business-integrity-card-actions">
              <button className="btn btn-danger" disabled={!importRetentionPolicy?.isEffective || !confirmed || !approvalReference.trim() || reason.trim().length < 10} onClick={() => void execute()}>Execute retention</button>
            </div>
          </section>

          <section className="enterprise-card business-integrity-card business-integrity-action-card">
            <CardHeading
              title="Source-row legal hold"
              description="A legal hold prevents retention disposal. Patient and source fields are not displayed here."
            />
            <div className="business-finance-form-grid business-integrity-form-grid">
              <label className="full">Immutable source-row UUID<input value={rowId} onChange={(event) => setRowId(event.target.value)} /></label>
              <label>Action<select value={holdEnabled ? 'APPLY' : 'RELEASE'} onChange={(event) => setHoldEnabled(event.target.value === 'APPLY')}><option value="APPLY">Apply hold</option><option value="RELEASE">Release hold</option></select></label>
              <label className="full">Reason<textarea value={holdReason} onChange={(event) => setHoldReason(event.target.value)} /></label>
            </div>
            <div className="business-integrity-card-actions">
              <button className="btn btn-primary" disabled={!rowId.trim() || holdReason.trim().length < 10} onClick={() => void changeHold()}>{holdEnabled ? 'Apply legal hold' : 'Release legal hold'}</button>
            </div>
          </section>
        </div>
      )}

      <section className="enterprise-card business-integrity-card">
        <CardHeading
          title="Backup, restore & migration recovery evidence"
          description="Only checksums, counts and safe evidence references are retained."
        />
        <div className="business-finance-table-wrap">
          <table className="business-finance-table">
            <thead><tr><th>Completed</th><th>Kind</th><th>Status</th><th>Migrations</th><th>Tables / rows</th><th>Evidence</th></tr></thead>
            <tbody>
              {(report?.recovery || []).map((item) => (
                <tr key={item.id}>
                  <td>{formatDate(item.completedAt)}</td>
                  <td>{item.verificationKind}</td>
                  <td><span className={`status-badge ${item.status === 'PASSED' ? 'active' : 'error'}`}>{item.status}</span></td>
                  <td>{item.sourceMigrationCount} / {item.restoredMigrationCount}</td>
                  <td>{item.businessTableCount} / {item.comparedRowCount}</td>
                  <td>{item.evidenceReference}<small className="business-finance-subline">SHA-256 {item.manifestSha256.slice(0, 12)}…</small></td>
                </tr>
              ))}
              {!report?.recovery.length && <tr><td colSpan={6}>No recovery rehearsal has been recorded yet.</td></tr>}
            </tbody>
          </table>
        </div>
      </section>

      {alert && <AdminAlertModal {...alert} onClose={() => setAlert(null)} />}
    </section>
  );
}

function CardHeading({ title, description, action }: { title: string; description: string; action?: ReactNode }) {
  return (
    <div className="enterprise-card-heading business-integrity-card-heading">
      <div><h3>{title}</h3><p>{description}</p></div>
      {action}
    </div>
  );
}

function Kpi({ label, value }: { label: string; value: number }) {
  return <div className="enterprise-card business-finance-kpi business-integrity-kpi"><span>{label}</span><strong>{value.toLocaleString('en-IN')}</strong></div>;
}

function formatDate(value: string) {
  return new Date(value).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' });
}

function errorMessage(error: unknown) {
  return error instanceof Error ? error.message : String(error || 'Unknown error');
}
